import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { objectToCamel } from 'ts-case-convert';
import { NotificationSettingsHttpService } from './notification-settings.http.service';
import {
  IClientNotificationEventRow,
  IClientNotificationOptions,
  IClientNotificationRule,
  INotificationCategoryGroup,
  INotificationPreference,
  INotificationText,
  INotificationTextDrafts,
  INotificationTextTranslation,
  INotificationTextUpdate,
} from 'src/app/models';
import { NotificationCategory, NotificationTextLanguage } from 'src/app/models/enums';

const VARIABLE_TOKEN_PATTERN = /\{([^{}]*)\}/g;

@Injectable({
  providedIn: 'root',
})
export class NotificationSettingsService {
  constructor(private notificationSettingsHttpService: NotificationSettingsHttpService) {}

  async getClientRules(clientId: number): Promise<IClientNotificationRule[]> {
    const response = await lastValueFrom(this.notificationSettingsHttpService.getClientRules(clientId));
    return (objectToCamel(response as object) as { rules: IClientNotificationRule[] }).rules;
  }

  async updateClientRules(clientId: number, rules: IClientNotificationRule[]): Promise<IClientNotificationRule[]> {
    const response = await lastValueFrom(this.notificationSettingsHttpService.updateClientRules(clientId, rules));
    return (objectToCamel(response as object) as { rules: IClientNotificationRule[] }).rules;
  }

  async getClientOptions(clientId: number): Promise<IClientNotificationOptions> {
    const response = await lastValueFrom(this.notificationSettingsHttpService.getClientOptions(clientId));
    return objectToCamel(response as object) as IClientNotificationOptions;
  }

  async updateClientOptions(
    clientId: number,
    options: IClientNotificationOptions,
  ): Promise<IClientNotificationOptions> {
    const response = await lastValueFrom(this.notificationSettingsHttpService.updateClientOptions(clientId, options));
    return objectToCamel(response as object) as IClientNotificationOptions;
  }

  async getOwnPreferences(clientId?: number): Promise<INotificationPreference[]> {
    const response = await lastValueFrom(this.notificationSettingsHttpService.getOwnPreferences(clientId));
    return (objectToCamel(response as object) as { preferences: INotificationPreference[] }).preferences;
  }

  async updateOwnPreferences(
    preferences: INotificationPreference[],
    clientId?: number,
  ): Promise<INotificationPreference[]> {
    const response = await lastValueFrom(
      this.notificationSettingsHttpService.updateOwnPreferences(preferences, clientId),
    );
    return (objectToCamel(response as object) as { preferences: INotificationPreference[] }).preferences;
  }

  async getClientTexts(clientId: number): Promise<INotificationText[]> {
    const response = await lastValueFrom(this.notificationSettingsHttpService.getClientTexts(clientId));
    return (objectToCamel(response as object) as { texts: INotificationText[] }).texts;
  }

  async updateClientTexts(clientId: number, updates: INotificationTextUpdate[]): Promise<INotificationText[]> {
    const response = await lastValueFrom(this.notificationSettingsHttpService.updateClientTexts(clientId, updates));
    return (objectToCamel(response as object) as { texts: INotificationText[] }).texts;
  }

  getTranslation(text: INotificationText, language: NotificationTextLanguage): INotificationTextTranslation {
    return (
      text.translations.find((translation) => translation.language === language) ?? {
        language,
        defaultText: '',
        customText: null,
      }
    );
  }

  getSentText(text: INotificationText, language: NotificationTextLanguage): string {
    const translation = this.getTranslation(text, language);
    return translation.customText ?? translation.defaultText;
  }

  findUnknownVariables(text: INotificationText, value: string): string[] {
    const unknownVariables = [...value.matchAll(VARIABLE_TOKEN_PATTERN)]
      .map((match) => match[1])
      .filter((variable) => !text.variables.includes(variable));
    return [...new Set(unknownVariables)];
  }

  buildTextUpdates(texts: INotificationText[], drafts: INotificationTextDrafts): INotificationTextUpdate[] {
    const textsByKey = new Map(texts.map((text) => [text.key, text]));
    return (Object.entries(drafts) as [NotificationTextLanguage, Record<string, string>][]).flatMap(
      ([language, valuesByKey]) =>
        Object.entries(valuesByKey).flatMap(([key, value]) => {
          const text = textsByKey.get(key);
          if (!text) return [];
          const translation = this.getTranslation(text, language);
          const customText = value.trim() === '' || value === translation.defaultText ? null : value;
          return customText === translation.customText ? [] : [{ key, language, text: customText }];
        }),
    );
  }

  groupByCategory<T extends { category: NotificationCategory }>(items: T[]): INotificationCategoryGroup<T>[] {
    return Object.values(NotificationCategory)
      .map((category) => ({ category, items: items.filter((item) => item.category === category) }))
      .filter((group) => group.items.length > 0);
  }

  toEventRows(rules: IClientNotificationRule[]): INotificationCategoryGroup<IClientNotificationEventRow>[] {
    const rowsByEvent = new Map<string, IClientNotificationEventRow & { category: NotificationCategory }>();
    rules.forEach((rule) => {
      const row = rowsByEvent.get(rule.event) ?? {
        event: rule.event,
        category: rule.category,
        mandatory: rule.mandatory,
        rulesByRecipient: {},
      };
      row.rulesByRecipient[rule.recipient] = rule;
      rowsByEvent.set(rule.event, row);
    });
    return this.groupByCategory([...rowsByEvent.values()]);
  }
}
